import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, IconButton, Collapse } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  useGetConfigurationsQuery,
  useAddConfigurationMutation,
  useUpdateConfigurationMutation,
  useDeleteConfigurationMutation,
} from "../UserSettings/scheduleApiSlice";

const SECTION_SX = {
  fontSize: "0.64rem",
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.4)",
  mb: "14px",
};

const FIELD_LABEL_SX = {
  fontSize: "0.6rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.3)",
  mb: "6px",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0d1117",
    borderRadius: "6px",
    fontSize: "0.88rem",
    color: "#e0e6f0",
    "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.22)" },
    "&.Mui-focused fieldset": { borderColor: "#FDB940 !important", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input": { padding: "8px 12px" },
};

function Field({ label, value, onChange, helperText }) {
  return (
    <Box>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      <TextField
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        fullWidth
        size="small"
        sx={INPUT_SX}
      />
      {helperText && (
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", mt: "4px" }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function FieldRow({ children, mt = "16px" }) {
  return (
    <Box sx={{ display: "flex", gap: "16px", mt }}>
      {React.Children.map(children, (child) => (
        <Box sx={{ flex: 1 }}>{child}</Box>
      ))}
    </Box>
  );
}

export default function ConfigurationManager() {
  const { data: configs = [], isSuccess } = useGetConfigurationsQuery();
  const [addConfiguration] = useAddConfigurationMutation();
  const [deletingId, setDeletingId] = useState(null);

  // Send only the name. The backend fills every other field from the default profile, so
  // a new profile starts as a copy of it. It used to post zeros here, which was actively
  // dangerous: a schedule linked to a zero-filled profile overwrites the default's
  // thresholds with 0 rather than falling back to them, putting every device above the
  // shift threshold and switching the lower limit off. (The old integration_period of 900
  // did not match the default's 30 either.)
  const handleAdd = () => {
    addConfiguration({ body: { description: "New Configuration" } });
  };

  if (!isSuccess) return null;

  return (
    <Box>
      {/* Section header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "20px" }}>
        <Box>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", lineHeight: 1.3 }}>
            Configuration Profiles
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.4)", mt: "3px" }}>
            Reusable HWI thresholds &amp; system parameters. Each schedule picks one profile.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem !important", color: "#0d1117" }} />}
          onClick={handleAdd}
          sx={{
            bgcolor: "#FDB940",
            color: "#0d1117",
            fontWeight: 600,
            fontSize: "0.82rem",
            textTransform: "none",
            borderRadius: "8px",
            boxShadow: "none",
            px: "14px",
            py: "7px",
            "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
          }}
        >
          Add Profile
        </Button>
      </Box>

      {configs.length === 0 && (
        <Typography sx={{ textAlign: "center", py: 4, color: "rgba(255,255,255,0.35)", fontSize: "0.88rem" }}>
          No configuration profiles yet. Click "Add Profile" to create one.
        </Typography>
      )}

      {configs.map((config, i) => (
        <ConfigCard
          key={config.id}
          config={config}
          defaultOpen={i === 0}
          deletingId={deletingId}
          setDeletingId={setDeletingId}
        />
      ))}
    </Box>
  );
}

function ConfigCard({ config, defaultOpen, deletingId, setDeletingId }) {
  const [updateConfiguration] = useUpdateConfigurationMutation();
  const [deleteConfiguration] = useDeleteConfigurationMutation();
  const [expanded, setExpanded] = useState(defaultOpen);
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    setForm({
      description:          config.description          ?? "",
      thermo_start:         config.thermo_start         ?? "",
      thermo_end:           config.thermo_end           ?? "",
      shift_start:          config.shift_start          ?? "",
      shift_end:            config.shift_end            ?? "",
      shave_start:          config.shave_start          ?? "",
      shave_end:            config.shave_end            ?? "",
      integration_period:   config.integration_period   ?? "",
      lower_limit:          config.lower_limit          ?? "",
      reliability_threshhold: config.reliability_threshhold ?? "",
      return_rate_all:      config.return_rate_all      ?? "",
      return_rate_off:      config.return_rate_off      ?? "",
      max_return:           config.max_return           ?? "",
    });
    setDirty(false);
  }, [config]);

  const set = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    updateConfiguration({ id: config.id, body: form });
    setDirty(false);
  };

  // The backend refuses to delete the default profile, or any profile a schedule still
  // points at, and returns 409 naming the schedules. Show that message rather than
  // failing silently — the operator has to go and unlink them first.
  const handleDelete = async () => {
    try {
      await deleteConfiguration({ id: config.id }).unwrap();
      setDeletingId(null);
      setDeleteError(null);
    } catch (err) {
      setDeleteError(
        err?.data?.detail || "This profile could not be deleted."
      );
    }
  };

  return (
    <Box sx={{ mb: "10px" }}>
      <Box
        sx={{
          backgroundColor: "#1C232C",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* Card header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            px: "20px",
            py: "14px",
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.02)" },
          }}
          onClick={() => setExpanded((v) => !v)}
        >
          <SettingsIcon sx={{ fontSize: "1.1rem", color: "#FDB940", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff", flexGrow: 1 }}>
            {form.description || config.description || `Configuration #${config.id}`}
          </Typography>

          {/* Default badge — the name is free text and says nothing about which profile
              governs unlinked schedules, so the flag has to be visible on its own. */}
          {config.is_default && (
            <Box
              sx={{
                px: "8px",
                py: "3px",
                border: "1px solid rgba(253,185,64,0.35)",
                borderRadius: "6px",
                backgroundColor: "rgba(253,185,64,0.12)",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#FDB940" }}>
                Default
              </Typography>
            </Box>
          )}

          {/* ID badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              px: "8px",
              py: "3px",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "6px",
              backgroundColor: "rgba(255,255,255,0.05)",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)" }}>
              ID
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#e0e6f0" }}>
              {config.id}
            </Typography>
          </Box>

          <IconButton
            size="small"
            sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { bgcolor: "transparent" } }}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          >
            {expanded ? <ExpandLessIcon sx={{ fontSize: "1.1rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "1.1rem" }} />}
          </IconButton>
        </Box>

        {/* Expanded content */}
        <Collapse in={expanded}>
          <Box sx={{ px: "24px", pb: "20px" }}>

            {/* Profile name */}
            <Box sx={{ mb: "20px" }}>
              <Typography sx={FIELD_LABEL_SX}>Profile Name / Description</Typography>
              <TextField
                value={form.description}
                onChange={(e) => set("description")(e.target.value)}
                fullWidth
                size="small"
                sx={INPUT_SX}
              />
            </Box>

            {/* HWI Thresholds */}
            <Typography sx={SECTION_SX}>Hot Water Index (HWI) Thresholds</Typography>
            <FieldRow mt="0">
              <Field label="Thermo Start" value={form.thermo_start} onChange={set("thermo_start")} />
              <Field label="Thermo End"   value={form.thermo_end}   onChange={set("thermo_end")} />
              <Box /><Box />
            </FieldRow>

            {/* Load Shifting */}
            <Typography sx={{ ...SECTION_SX, mt: "20px" }}>Load Shifting HWI</Typography>
            <FieldRow mt="0">
              <Field label="Shift Start" value={form.shift_start} onChange={set("shift_start")} />
              <Field label="Shift End"   value={form.shift_end}   onChange={set("shift_end")} />
              <Box /><Box />
            </FieldRow>

            {/* Peak Shaving */}
            <Typography sx={{ ...SECTION_SX, mt: "20px" }}>Peak Shaving HWI</Typography>
            <FieldRow mt="0">
              <Field label="Shave Start" value={form.shave_start} onChange={set("shave_start")} />
              <Field label="Shave End"   value={form.shave_end}   onChange={set("shave_end")} />
              <Box /><Box />
            </FieldRow>

            {/* Divider */}
            <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", my: "20px" }} />

            {/* System Parameters */}
            <Typography sx={SECTION_SX}>System Parameters</Typography>
            <FieldRow mt="0">
              <Field label="Integration Period"    value={form.integration_period}     onChange={set("integration_period")} />
              <Field label="Lower Limit"           value={form.lower_limit}            onChange={set("lower_limit")} helperText="Set to 0 to disable" />
              <Field label="Reliability Threshold" value={form.reliability_threshhold} onChange={set("reliability_threshhold")} />
              <Field label="Max Return"            value={form.max_return}             onChange={set("max_return")} />
            </FieldRow>
            <FieldRow>
              <Field label="Return Rate All" value={form.return_rate_all} onChange={set("return_rate_all")} />
              <Field label="Return Rate Off" value={form.return_rate_off} onChange={set("return_rate_off")} />
              <Box /><Box />
            </FieldRow>

            {/* Footer */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "20px" }}>
              {/* The default profile cannot be deleted — the stack has nothing to fall
                  back on without it. Edit it in place instead. */}
              {config.is_default ? (
                <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.3)" }}>
                  The default profile cannot be deleted
                </Typography>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => setDeletingId(config.id)}
                  sx={{
                    color: "rgba(255,255,255,0.3)",
                    "&:hover": { color: "#f87171", bgcolor: "rgba(248,113,113,0.08)" },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              )}
              <Button
                variant="contained"
                disabled={!dirty}
                onClick={handleSave}
                sx={{
                  bgcolor: "#FDB940",
                  color: "#0d1117",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  boxShadow: "none",
                  px: "20px",
                  py: "6px",
                  "&:hover": { bgcolor: "#e8a830", boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "rgba(253,185,64,0.25)", color: "rgba(0,0,0,0.4)" },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Inline delete confirmation */}
      <Collapse in={deletingId === config.id}>
        <Box
          sx={{
            mt: "4px",
            px: "20px",
            py: "12px",
            backgroundColor: "rgba(248,113,113,0.06)",
            border: "1px solid rgba(248,113,113,0.2)",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: "0.84rem", color: deleteError ? "#f87171" : "rgba(255,255,255,0.65)" }}>
            {deleteError || (
              <>
                Delete <strong style={{ color: "#e0e6f0" }}>"{config.description || `Configuration #${config.id}`}"</strong>? This cannot be undone.
              </>
            )}
          </Typography>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              size="small"
              onClick={() => { setDeletingId(null); setDeleteError(null); }}
              sx={{ fontSize: "0.8rem", textTransform: "none", color: "rgba(255,255,255,0.5)", "&:hover": { bgcolor: "rgba(255,255,255,0.06)" } }}
            >
              {deleteError ? "Close" : "Cancel"}
            </Button>
            {/* Nothing to retry until the operator unlinks it, so drop the confirm. */}
            <Button
              size="small"
              onClick={handleDelete}
              sx={{
                display: deleteError ? "none" : undefined,
                fontSize: "0.8rem",
                textTransform: "none",
                color: "#f87171",
                border: "1px solid rgba(248,113,113,0.35)",
                borderRadius: "6px",
                px: "10px",
                "&:hover": { bgcolor: "rgba(248,113,113,0.1)", borderColor: "#f87171" },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
